// --------------------------------------------------------------
// Created On: 2025-03-11
// Author: Hannah Vaughan
//
// Last Modified: 2026-01-06
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import SalesOrderDetails from "../../../components/SalesOrderDetails/SalesOrderDetails";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import useApi from "../../../hooks/useApi";
import { API } from "../../../constants/miscellaneous";
import getApiError from "../../../utilities/api/getApiError";

// Modal to display information on a completed sales order that is accessible from the Order History Page.
export default function OrderDetailsModal(props: Props): Component {
  const [shippingCountry, setShippingCountry] = useState<string>("");
  const [shippingCompanyName, setShippingCompanyName] = useState<string>("");
  const [shippingAddressLine1, setShippingAddressLine1] = useState<string>("");
  const [shippingAddressLine2, setShippingAddressLine2] = useState<string>("");
  const [shippingContactName, setShippingContactName] = useState<string>("");
  const [shippingPhoneNumber, setShippingPhoneNumber] = useState<string>("");
  const [shippingCity, setShippingCity] = useState<string>("");
  const [shippingState, setShippingState] = useState<string>("");
  const [shippingZipCode, setShippingZipCode] = useState<string>("");
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [itemsSubtotal, setItemsSubtotal] = useState<string>("");
  const [shipBy, setShipBy] = useState<string>("");
  const [grandTotal, setGrandTotal] = useState<string>("");
  const [orderDate, setOrderDate] = useState<string>("");
  const [companyDiscount, setCompanyDiscount] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [terms, setTerms] = useState<string>("");
  const [fob, setFob] = useState<string>("");
  const [salesPerson, setSalesPerson] = useState<string>("");
  const [shipVia, setShipVia] = useState<string>("");
  const [billingType, setBillingType] = useState<string>("");
  const [trackingNumber, setTrackingNumber] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [invoiceNumber, setInvoiceNumber] = useState<number | undefined>(undefined);
  const [shipDate, setShipDate] = useState<string | undefined>(undefined);
  const [shippingCharges, setShippingCharges] = useState<number | undefined>(undefined);
  const [dueDate, setDueDate] = useState<string | undefined>(undefined);
  const [invoicedAmount, setInvoicedAmount] = useState<number | undefined>(undefined);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);

  // Gets completed order information when a specific sales order is selected.
  useApi(
    () => {
      props.onChangeLoading(true);
      return true;
    },
    {
      url: `${API}/completed-order/${props.orderNumber}`,
      method: "GET",
    },
    async (response: Response, responseBody: CompletedOrderResponseBody) => {
      if (response.ok && responseBody) {
        setShippingCountry(responseBody.shippingCountry);
        setShippingCompanyName(responseBody.shippingCompanyName);
        setShippingAddressLine1(responseBody.shippingAddressLine1);
        setShippingAddressLine2(responseBody.shippingAddressLine2);
        setShippingContactName(responseBody.shippingContactName);
        setShippingPhoneNumber(responseBody.shippingPhoneNumber);
        setShippingCity(responseBody.shippingLocality);
        setShippingState(responseBody.shippingAdministrativeArea);
        setShippingZipCode(responseBody.shippingPostalCode);
        setSpecialInstructions(responseBody.specialInstructions);
        setItemsSubtotal(responseBody.itemsSubtotal);
        setShipBy(responseBody.shipBy);
        setGrandTotal(responseBody.grandTotal);
        setOrderDate(responseBody.orderDate);
        setCompanyDiscount(responseBody.discountPercent);
        setCompanyName(responseBody.companyName);
        setInvoiceNumber(responseBody.invoiceNumber);
        setShipDate(responseBody.shipDate);
        setShippingCharges(responseBody.shippingCharges);
        setDueDate(responseBody.dueDate);
        setInvoicedAmount(responseBody.invoicedAmount);
        setTerms(responseBody.terms);
        setFob(responseBody.fob);
        setSalesPerson(responseBody.salesPerson);
        setShipVia(responseBody.shipVia);
        setBillingType(responseBody.billingType);
        setTrackingNumber(responseBody.trackingNumber);
        setStatus(responseBody.status);
        setOrderItems(responseBody.orderItems);
        props.onChangeFailedToLoad(false);
        props.onChangeErrorMessage("");
      } else {
        props.onChangeFailedToLoad(true);
        props.onChangeErrorMessage(await getApiError(response, "Unable to open order details."));
      }
      props.onChangeLoading(false);
    },
    []
  );

  // Clear all state variables before closing the modal.
  function exitModal(): void {
    setShippingCountry("");
    setShippingCompanyName("");
    setShippingAddressLine1("");
    setShippingAddressLine2("");
    setShippingContactName("");
    setShippingPhoneNumber("");
    setShippingCity("");
    setShippingState("");
    setShippingZipCode("");
    setSpecialInstructions("");
    setItemsSubtotal("");
    setShipBy("");
    setGrandTotal("");
    setOrderDate("");
    setCompanyDiscount("");
    setCompanyName("");
    setInvoiceNumber(undefined);
    setShipDate(undefined);
    setShippingCharges(undefined);
    setDueDate(undefined);
    setInvoicedAmount(undefined);
    setTerms("");
    setFob("");
    setSalesPerson("");
    setShipVia("");
    setBillingType("");
    setTrackingNumber("");
    setStatus("");
    setOrderItems([]);
    props.onChangeFailedToLoad(false);
    props.onChangeErrorMessage("");
    props.onClose();
  }

  return (
    <Modal
      show={true}
      onHide={() => exitModal()}
      backdropClassName=""
      style={{ zIndex: "var(--standard-modal-z-index)" }}
      size="xl"
      centered
      animation
    >
      <ModalHeader>
        <h5 className="font-weight-bold">Order Details</h5>
      </ModalHeader>

      <ModalBody>
        <SalesOrderDetails
          showHeadersAndFooters={false}
          orderNumber={props.orderNumber}
          poNumber={props.poNumber}
          loading={props.loading}
          shippingCountry={shippingCountry}
          shippingCompanyName={shippingCompanyName}
          shippingAddressLine1={shippingAddressLine1}
          shippingAddressLine2={shippingAddressLine2}
          shippingContactName={shippingContactName}
          shippingPhoneNumber={shippingPhoneNumber}
          shippingCity={shippingCity}
          shippingState={shippingState}
          shippingZipCode={shippingZipCode}
          specialInstructions={specialInstructions}
          itemsSubtotal={itemsSubtotal}
          shipBy={shipBy}
          grandTotal={grandTotal}
          orderDate={orderDate}
          companyDiscount={companyDiscount}
          companyName={companyName}
          invoiceNumber={invoiceNumber}
          shipDate={shipDate}
          shippingCharges={shippingCharges}
          dueDate={dueDate}
          invoicedAmount={invoicedAmount}
          orderItems={orderItems}
          terms={terms}
          fob={fob}
          salesPerson={salesPerson}
          shipVia={shipVia}
          billingType={billingType}
          trackingNumber={trackingNumber}
          status={status}
          onChangeLoading={(loading) => props.onChangeLoading(loading)}
          onChangeFailedToLoad={(failedToLoad) => props.onChangeFailedToLoad(failedToLoad)}
          onChangeErrorMessage={(errorMessage) => props.onChangeErrorMessage(errorMessage)}
        />
      </ModalBody>

      <ModalFooter>
        <button className="btn btn-primary" type="button" onClick={() => exitModal()}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface Props {
  orderNumber: string;
  poNumber: string;
  loading: boolean;
  onClose: () => void;
  onChangeLoading: (loading: boolean) => void;
  onChangeFailedToLoad: (failedToLoad: boolean) => void;
  onChangeErrorMessage: (errorMessage: string) => void;
}

interface CompletedOrderResponseBody {
  shippingCountry: string;
  shippingAdministrativeArea: string;
  shippingLocality: string;
  shippingDependentLocality: string;
  shippingPostalCode: string;
  shippingSortingCode: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  specialInstructions: string;
  discountPercent: string;
  companyName: string;
  shipBy: string;
  poNumber: string;
  orderDate: string;
  grandTotal: string;
  itemsSubtotal: string;
  invoiceNumber?: number;
  shipDate?: string;
  shippingCharges?: number;
  dueDate?: string;
  invoicedAmount?: number;
  terms: string;
  fob: string;
  salesPerson: string;
  shipVia: string;
  billingType: string;
  trackingNumber: string;
  status: string;
  orderItems: OrderItem[];
}

interface OrderItem {
  partNumber: string;
  partDescription: string;
  paintColor: string;
  impellerTrim: string;
  impellerAngle: string;
  price: string;
  quantity: string;
}
